import React, {FC, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Alert} from 'reactstrap';
import {Route, Routes} from 'react-router-dom';
import {AppRegion} from '@touchnet/react-a11y';
import TnLeftNav from '@touchnet/react-left-nav';

import AccountingCodesPage from '../pages/accounting-codes-page';
import UpdateCommonACPage from '../pages/update-common-ac-page';
import TermsPage from '../pages/terms-page';
import LogoutPage from "~/components/pages/logout-page";
import routes, {getRoute} from '~/route-map';
import UserMgmtPage from "~/components/pages/user-mgmt/user-mgmt-page";
import useEffectOnce from "~/hooks/use-effect-once";
import Selectors from "~/state/selectors.ts";
import {actions} from "~/state/reducers/settings-reducer.ts";

export const AppLayout: FC = () => {
    const cnum: string  = useSelector(Selectors.getCustomerNumber);
    const ctx: string = useSelector(Selectors.getAppContext);
    const settings = useSelector(Selectors.getSettings);

    const dispatch = useDispatch();
    const mainUrl = getRoute(routes.app.base,{cnum, ctx});

    useEffectOnce(() => {
        dispatch(actions.loadSettings());
    });

    const hasWorktagLink = useMemo<boolean>(
        () => {
            return !!(settings.links
                && settings.links.chartfields
                && settings.links.chartfields.href);
        }, [settings]);

    const getNavLinks = () => {
        const navLinks = [];
        navLinks.push({display: 'Terms', path: `${mainUrl}/terms`});
        navLinks.push({display: 'User Management', path: `${mainUrl}/admin/users`});
        if (hasWorktagLink) {
            navLinks.push({display: 'Accounting Codes', path: `${mainUrl}/ac/codes`});
        }
        return navLinks;
    }

    if (!settings.meta.loaded) {
        return (
            <Alert className="m-4" color="info">Loading...</Alert>
        );
    }

    if (settings.meta.loaded && settings.meta.noPermissions) {
        return (
            <Alert className="m-4" color="danger">You do not have permission to access this page. Please contact your system administrator.</Alert>
        );
    }

    return (
        <section id="main" className="flex-grow-1 container-fluid d-flex flex-column">
            <div className="row flex-grow-1">
                <AppRegion id="page-nav" name="left navigation" className="col-12 col-md-2">
                    <TnLeftNav links={getNavLinks()}/>
                </AppRegion>
                <AppRegion id="page-body" name="main content" className="col-12 col-md-10">
                    <Routes>
                        <Route 
                            path={`/terms`}
                            element={<TermsPage />}
                        />
                        <Route
                            path={`/admin/users/*`}
                            element={<UserMgmtPage />}
                        />
                        <Route
                            path={`/ac/codes/add`}
                            element={<UpdateCommonACPage title="Add Accounting Code" />}
                        />
                        <Route
                            path={`/ac/codes/:codeId`}
                            element={<UpdateCommonACPage title="Edit Accounting Code" />}
                        />
                        <Route
                            path={`/ac/*`}
                            element={<AccountingCodesPage />}
                        />
                    </Routes>
                </AppRegion>
            </div>
        </section>
    );
}

export default AppLayout
